/**
 * IM Store
 * 只负责 IM 连接状态管理
 *
 * 职责：
 * 1. 初始化和管理 IM 连接
 * 2. 监听连接状态事件（connected, disconnected, kickOut, authFailed）
 * 3. 提供连接、断开、销毁等操作
 *
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { imManager } from '@/managers/IMManager'
// import { messageSyncManager } from '@/managers/MessageSyncManager'
import { useUserStore } from '@/stores/user'
// import { getAllConversations } from '@/indexdb/chat/conversationUtils'
import { showToast } from 'vant'

export const useIMStore = defineStore('im', () => {

  const userStore = useUserStore()

  // ========== State ==========
  const isConnected = ref(false) // IM 是否连接成功
  const isConnecting = ref(false) // IM 是否正在连接
  const isSyncing = ref(false) // IM 是否正在同步离线消息
  const connectionError = ref(null) // IM 连接错误信息

  // ========== Actions ==========

  /**
   * 初始化 IM（只负责初始化和连接）
   * 包括：IMSDK初始化、建连
   */
  async function initIM() {
    const { zy_token, zy_im_config, zy_im_open } = userStore.getImData()

    // 检查 IM 是否开启
    if (!zy_im_open) {
      console.log('[IM Store] IM is not enabled')
      return
    }

    // 检查必要参数
    if (!zy_token || !zy_im_config) {
      console.error('[IM Store] Missing IM credentials')
      return
    }

    const userId = userStore.getId()
    if (!userId) {
      console.error('[IM Store] User ID not found')
      return
    }

    try {
      console.log('[IM Store] Initializing IM...')
      isConnecting.value = true
      connectionError.value = null

      // 0. 先销毁旧实例（防止用户切换时数据泄漏）
      if (imManager.isInitialized) {
        console.log('[IM Store] Destroying previous IM instance before re-initialization')
        imManager.destroy()
      }

      // 1. 初始化 IMManager（会自动注册 SDK 事件监听）
      await imManager.initialize({
        zy_token,
        userId,
        httpUrl: import.meta.env.VITE_API_BASE_URL,
        zy_im_config
      })

      // 2. 建立连接
      await imManager.connect()

      console.log('[IM Store] IM initialization requested')

      // ✅ 状态更新由 IMConnectionRouter Plugin 处理
      // ✅ 消息处理由 IMMessageRouter Plugin 处理

    } catch (error) {
      console.error('[IM Store] Failed to initialize IM:', error)
      connectionError.value = error.message
      showToast('Failed to connect to chat server')
    } finally {
      isConnecting.value = false
    }
  }


  /**
   * 同步离线消息
   */
  // async function syncOfflineMessages() {
  //   if (isSyncing.value) {
  //     console.warn('[IM Store] Already syncing')
  //     return
  //   }

  //   isSyncing.value = true

  //   try {
  //     console.log('[IM Store] Syncing offline messages...')

  //     const result = await messageSyncManager.syncOfflineMessages()

  //     console.log('[IM Store] Offline messages synced:', result)

  //     // 刷新会话列表
  //     const { useConversationStore } = await import('@/stores/conversation')
  //     const conversationStore = useConversationStore()
  //     conversationStore.conversations = await getAllConversations()

  //   } catch (error) {
  //     console.error('[IM Store] Failed to sync offline messages:', error)
  //   } finally {
  //     isSyncing.value = false
  //   }
  // }

  /**
   * 重新连接 IM
   */
  async function reconnectIM() {
    const { zy_token, zy_im_config } = userStore.getImData()
    const userId = userStore.getId()

    if (!zy_token || !zy_im_config || !userId) {
      console.error('[IM Store] Cannot reconnect: missing credentials')
      return
    }

    try {
      isConnecting.value = true
      connectionError.value = null

      await imManager.reconnect({
        zy_token,
        userId,
        httpUrl: import.meta.env.VITE_API_BASE_URL,
        zy_im_config
      })

      console.log('[IM Store] Reconnected successfully')
    } catch (error) {
      console.error('[IM Store] Failed to reconnect:', error)
      connectionError.value = error.message
    } finally {
      isConnecting.value = false
    }
  }

  /**
   * 断开 IM 连接
   */
  function disconnectIM() {
    console.log('[IM Store] Disconnecting IM...')
    imManager.disconnect()
    isConnected.value = false
    isConnecting.value = false
  }

  /**
   * 建立 IM 连接
   */
  function connectIM() {
    console.log('[IM Store] Connecting IM...')
    imManager.connect()
    isConnected.value = true
    isConnecting.value = true
  }

  /**
   * 销毁 IM
   */
  function destroyIM() {
    console.log('[IM Store] Destroying IM...')

    // 销毁 IMManager（会自动清理 IMManager 的事件监听器）
    imManager.destroy()

    // 重置状态
    isConnected.value = false
    isConnecting.value = false
    connectionError.value = null
  }

  return {
    // State
    isConnected,
    isConnecting,
    isSyncing,
    connectionError,

    // Actions
    initIM,
    reconnectIM,
    disconnectIM,
    connectIM,
    destroyIM,
    // syncOfflineMessages
  }
})
